import type { UserUiUpdatePasswordQuery } from "../Models/Ui/User";
import type { UserUpdatePasswordQuery } from "@/Modules/Models/Domain/User";
import type { UserApiUpdatePasswordQuery } from "@/Modules/Models/Api/User";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { AppError } from "@/Modules/App/Models/Domain/AppError";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";

import { updateUserPassword } from "@/Services/TherapyApi/Users";

import useUserStore from "@/Modules/User/Store";
import useAppStore from "@/Modules/App/Store";

import { hasValue } from "@/Utils/Objects";
import { isAppError } from "@/Modules/App/Models/Typeguards/isAppError";
import { isApiError } from "@/Services/Api/Typeguards/isApiError";

async function updateUserPasswordAH(userUiUpdatePasswordQuery: UserUiUpdatePasswordQuery) {
  const userStore = useUserStore();
  const appStore = useAppStore();

  userStore.isLoading = true;
  userStore.notification = null;

  let userUpdatePasswordQuery;

  try {
    userUpdatePasswordQuery =
      transformUserUiUpdatePasswordQueryToUserUpdatePasswordQuery(userUiUpdatePasswordQuery);
  } catch (error: unknown) {
    if (isAppError(error)) {
      userStore.notification = {
        type: NotificationType.ERROR,
        message: error.i18nKey || error.message,
        code: null,
      };
    }
  } finally {
    userStore.isLoading = false;
  }

  try {
    if (hasValue(userUpdatePasswordQuery)) {
      const userId = userStore.user ? userStore.user.id : "";
      const userApiUpdatePasswordQuery: UserApiUpdatePasswordQuery =
        transformUserUpdatePasswordQueryToUserApiUpdatePasswordQuery(userUpdatePasswordQuery);

      await updateUserPassword(userId, userApiUpdatePasswordQuery);
      userStore.notification = {
        type: NotificationType.SUCCESS,
        message: "your_password_has_been_successfully_changed",
        code: null,
      };
    }
  } catch (error: unknown) {
    const code = isApiError(error) ? error["error_code"] : null;

    if (code === ApiErrorCodes.CANNOT_USER_SAME_PASSWORD) {
      userStore.notification = {
        type: NotificationType.ERROR,
        code,
        message: "account.password.error.the_current_and_new_password_are_the_same",
      };
    }
    if (code === ApiErrorCodes.WRONG_CREDENTIALS) {
      userStore.notification = {
        type: NotificationType.ERROR,
        code,
        message: "account.password.error.your_current_password_is_not_correct_please_try_again",
      };
    } else {
      appStore.showSnackbar({
        message: "there_was_an_error_connecting_to_the_server_please_try_again_later",
      });
    }
  } finally {
    userStore.isLoading = false;
  }
}

function transformUserUiUpdatePasswordQueryToUserUpdatePasswordQuery(
  userUiUpdatePasswordQuery: UserUiUpdatePasswordQuery
): UserUpdatePasswordQuery {
  if (userUiUpdatePasswordQuery.newPassword !== userUiUpdatePasswordQuery.confirmPassword) {
    throw new AppError(
      "reset_password.the_passwords_are_different",
      "account.password.error.the_current_and_new_password_are_the_same"
    );
  }
  const userUpdatePasswordQuery = {
    newPassword: userUiUpdatePasswordQuery.newPassword,
    oldPassword: userUiUpdatePasswordQuery.oldPassword,
  };
  return userUpdatePasswordQuery;
}

function transformUserUpdatePasswordQueryToUserApiUpdatePasswordQuery(
  userUpdatePasswordQuery: UserUpdatePasswordQuery
): UserApiUpdatePasswordQuery {
  return {
    old_password: userUpdatePasswordQuery.oldPassword,
    password: userUpdatePasswordQuery.newPassword,
  };
}

export default updateUserPasswordAH;
